import { Box, Typography } from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import { LabelText, ValueText } from '~/components/Fields/LabeledValue';
import ReviewStatus from '~/components/Structures/ReviewStatus';
import { RoiReadModel } from '~/domain/readmodels/StructureSetReadModel';

interface StructureTooltipPropertiesProps {
    roi: RoiReadModel;
    volumeCm3: number;
    equivalentSphereDiameterCm: number;
}

export default function StructureTooltipProperties({
    roi,
    volumeCm3,
    equivalentSphereDiameterCm,
}: StructureTooltipPropertiesProps) {
    const intl = useIntl();

    return (
        <Box>
            <Typography fontSize={12} fontWeight={500} mb={1}>
                <FormattedMessage id="structure.info" />
            </Typography>
            <Box
                display="grid"
                gridTemplateColumns="auto 1fr"
                gap="3px 6px"
                alignItems="baseline"
                sx={{
                    '& .MuiTypography-root': {
                        fontSize: 11,
                        lineHeight: 1.2,
                    },
                }}
            >
                <LabelText>
                    <FormattedMessage id="name" />
                </LabelText>
                <ValueText component="span" fontWeight={500}>
                    {roi.name}
                </ValueText>

                <LabelText>
                    <FormattedMessage id="structure.roiNumber" />
                </LabelText>
                <ValueText component="span">{roi.roiNumber}</ValueText>

                <LabelText>
                    <FormattedMessage id="approvalStatus" />
                </LabelText>
                <Box height={12} mt={-0.5}>
                    <ReviewStatus status={roi.approvalStatus} fontSize={12} />
                </Box>

                <LabelText>
                    <FormattedMessage id="structure.type" />
                </LabelText>
                <ValueText component="span">
                    {roi.interpretedType ||
                        intl.formatMessage({ id: 'unknown' })}
                </ValueText>

                <LabelText>
                    <FormattedMessage id="structure.algorithm" />
                </LabelText>
                <ValueText component="span">
                    {roi.roiGenerationAlgorithm}
                </ValueText>

                {!roi.isPointRoi && (
                    <>
                        <LabelText>
                            <FormattedMessage id="structure.volume" />
                        </LabelText>
                        <ValueText component="span">
                            {isFinite(volumeCm3)
                                ? `${volumeCm3.toFixed(2)} cm\u00B3`
                                : '\u2014'}
                        </ValueText>

                        <LabelText>
                            <FormattedMessage id="structure.eqSphereDiam" />
                        </LabelText>
                        <ValueText component="span">
                            {isFinite(equivalentSphereDiameterCm)
                                ? `${equivalentSphereDiameterCm.toFixed(2)} cm`
                                : '\u2014'}
                        </ValueText>

                        <LabelText>
                            <FormattedMessage id="structure.contours" />
                        </LabelText>
                        <ValueText component="span">
                            {roi.contourCount}
                        </ValueText>
                    </>
                )}

                <LabelText>
                    <FormattedMessage id="structure.electronDensity" />
                </LabelText>
                <ValueText
                    component="span"
                    data-testid="structure-electron-density-value"
                >
                    {roi.electronDensityOverride != null
                        ? roi.electronDensityOverride.toFixed(4)
                        : '\u2014'}
                </ValueText>

                <LabelText>
                    <FormattedMessage id="structure.massDensity" />
                </LabelText>
                <ValueText
                    component="span"
                    data-testid="structure-mass-density-value"
                >
                    {roi.massDensityOverride != null
                        ? roi.massDensityOverride.toFixed(4)
                        : '\u2014'}
                </ValueText>

                {roi.roiDescription && (
                    <>
                        <LabelText>
                            <FormattedMessage id="description" />
                        </LabelText>
                        <ValueText component="span" title={roi.roiDescription}>
                            {roi.roiDescription}
                        </ValueText>
                    </>
                )}

                {roi.identificationCode && (
                    <>
                        <LabelText>
                            <FormattedMessage id="structure.identificationCode" />
                        </LabelText>
                        <ValueText
                            component="span"
                            sx={{
                                whiteSpace: 'normal',
                                wordBreak: 'break-word',
                            }}
                        >
                            {roi.identificationCode.codeMeaning}
                            {' ('}
                            {roi.identificationCode.codeValue}
                            {') '}
                            {roi.identificationCode.codingSchemeDesignator}
                            {roi.identificationCode.codingSchemeVersion
                                ? ` (${roi.identificationCode.codingSchemeVersion})`
                                : ''}
                        </ValueText>
                    </>
                )}
            </Box>
        </Box>
    );
}
